/**
 * OWPML (KS X 6101) section parsing and surgical paragraph rewriting.
 *
 * HWPX is a ZIP of XML parts, so the part-preserving save pipeline built for
 * OOXML applies unchanged. What differs is where formatting lives: a run does
 * not carry its own properties, it carries an **id reference** into
 * `Contents/header.xml`. `<hp:run charPrIDRef="3">` means "whatever charPr 3
 * says", so a viewer that ignores the header renders a whole document in one
 * undifferentiated face — and one that rebuilds the header on save would
 * renumber references that other parts still point at.
 *
 * Hence: the header is read into lookup tables for display, and never written.
 * A text edit replaces the paragraph's byte span inside its section part and
 * keeps the id references exactly as they were, so the formatting a run
 * pointed at is still the formatting it points at (§13, §18, §24).
 */
import { escapeXml, unescapeXml } from '../ooxml/xml.js';
import { HandlerError } from '../contract.js';

/** HWPUNIT: 1/7200 inch. CSS pixels are 1/96 inch. */
export const HWPUNIT_PER_PX = 75;
/** Character heights are in 1/100 pt. */
const HEIGHT_TO_PX = 96 / 72 / 100;

export interface CharShape {
  readonly id: string;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly sizePx: number | null;
  readonly color: string | null;
  readonly fontRef: string | null;
}

export interface ParaShape {
  readonly id: string;
  readonly alignment: 'left' | 'center' | 'right' | 'justify' | null;
  /** Left indent in CSS pixels. */
  readonly indentPx: number;
  /** True when the paragraph is a list item under a numbering definition. */
  readonly numbered: boolean;
}

export interface StyleInfo {
  readonly id: string;
  readonly name: string;
  /** 1–6 when the style is a Hangul outline/heading level, else null. */
  readonly headingLevel: number | null;
}

export interface HwpxHeader {
  readonly charShapes: ReadonlyMap<string, CharShape>;
  readonly paraShapes: ReadonlyMap<string, ParaShape>;
  readonly styles: ReadonlyMap<string, StyleInfo>;
  /** Font face names by id, so a run can name the font it wanted (§25). */
  readonly fonts: ReadonlyMap<string, string>;
}

export type HwpxRun =
  | {
    readonly kind: 'text';
    readonly text: string;
    readonly charPrId: string | null;
  }
  | { readonly kind: 'break' }
  | { readonly kind: 'tab' }
  | {
    readonly kind: 'image';
    /** `binaryItemIDRef` — resolved against the manifest to a BinData part. */
    readonly itemId: string;
    readonly part: string | null;
    readonly widthPx: number;
    readonly heightPx: number;
    readonly alt: string;
  }
  | {
    /** An equation, chart, OLE object or control this release cannot render. */
    readonly kind: 'object';
    readonly what: string;
  };

export interface HwpxParagraph {
  readonly kind: 'paragraph';
  /** Position in document order across all sections. */
  readonly index: number;
  /** Section part this paragraph lives in. */
  readonly part: string;
  readonly charPrId: string | null;
  readonly paraPrId: string | null;
  readonly styleId: string | null;
  readonly runs: readonly HwpxRun[];
  readonly text: string;
  readonly originalText: string;
  readonly editable: boolean;
  readonly start: number;
  readonly end: number;
}

export interface HwpxTableCell {
  readonly paragraphs: readonly HwpxParagraph[];
  readonly colSpan: number;
  readonly rowSpan: number;
}

export interface HwpxTable {
  readonly kind: 'table';
  readonly rows: readonly (readonly HwpxTableCell[])[];
  readonly start: number;
  readonly end: number;
}

export type HwpxBlock = HwpxParagraph | HwpxTable;

export interface ParsedSection {
  readonly blocks: readonly HwpxBlock[];
  readonly paragraphs: readonly HwpxParagraph[];
  readonly truncated: boolean;
}

/* ------------------------------------------------------------------ *
 * scanning helpers
 * ------------------------------------------------------------------ */

function attr(tag: string, name: string): string | null {
  const found = new RegExp(`\\b${name}="([^"]*)"`).exec(tag);
  return found ? unescapeXml(found[1]!) : null;
}

function endOfElement(xml: string, open: number, name: string): number {
  const openTag = `<${name}`;
  const closeTag = `</${name}>`;
  const tagEnd = xml.indexOf('>', open);
  if (tagEnd < 0) return xml.length;
  if (xml[tagEnd - 1] === '/') return tagEnd + 1;

  let depth = 1;
  let cursor = tagEnd + 1;
  while (depth > 0) {
    const nextOpen = xml.indexOf(openTag, cursor);
    const nextClose = xml.indexOf(closeTag, cursor);
    if (nextClose < 0) return xml.length;
    if (nextOpen >= 0 && nextOpen < nextClose) {
      const nextTagEnd = xml.indexOf('>', nextOpen);
      if (nextTagEnd < 0) return xml.length;
      const after = xml[nextOpen + openTag.length]!;
      if ((after === '>' || after === ' ' || after === '/') && xml[nextTagEnd - 1] !== '/') {
        depth += 1;
      }
      cursor = nextTagEnd + 1;
      continue;
    }
    depth -= 1;
    cursor = nextClose + closeTag.length;
  }
  return cursor;
}

function indexOfElement(xml: string, tag: string, from: number, limit: number): number {
  let cursor = from;
  for (;;) {
    const found = xml.indexOf(tag, cursor);
    if (found < 0 || found >= limit) return -1;
    const after = xml[found + tag.length];
    if (after === '>' || after === ' ' || after === '/' || after === '\n' || after === '\r' || after === '\t') {
      return found;
    }
    cursor = found + tag.length;
  }
}

/* ------------------------------------------------------------------ *
 * header.xml
 * ------------------------------------------------------------------ */

/**
 * Read the formatting tables a section's id references point at.
 *
 * Returns empty tables rather than throwing when the header is missing: a
 * document with no header still has readable text, and refusing to show it
 * because the styling could not be resolved would be the wrong trade (§34).
 */
export function parseHeader(headerXml: string): HwpxHeader {
  const charShapes = new Map<string, CharShape>();
  for (const match of headerXml.matchAll(/<hh:charPr\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/hh:charPr>)/g)) {
    const attrs = match[1]!;
    const body = match[2] ?? '';
    const id = attr(attrs, 'id');
    if (!id) continue;
    const height = attr(attrs, 'height');
    const color = attr(attrs, 'textColor');
    charShapes.set(id, {
      id,
      // These are empty marker elements — present means on. Unlike OOXML there
      // is no `val="0"` form to guard against here.
      bold: /<hh:bold\b/.test(body),
      italic: /<hh:italic\b/.test(body),
      underline: /<hh:underline\b/.test(body),
      strike: /<hh:strikeout\b/.test(body),
      sizePx: height ? Number(height) * HEIGHT_TO_PX : null,
      color: color && color !== 'none' ? color.replace(/^#/, '') : null,
      fontRef: /<hh:fontRef\b[^>]*\bhangul="([^"]*)"/.exec(body)?.[1] ?? null,
    });
  }

  const paraShapes = new Map<string, ParaShape>();
  for (const match of headerXml.matchAll(/<hh:paraPr\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/hh:paraPr>)/g)) {
    const attrs = match[1]!;
    const body = match[2] ?? '';
    const id = attr(attrs, 'id');
    if (!id) continue;
    // Alignment appears as an attribute in some writers and a child element in
    // others; both forms are in the wild.
    const align = attr(attrs, 'align')
      ?? /<hh:align\b[^>]*\bhorizontal="([^"]+)"/.exec(body)?.[1]
      ?? null;
    const left = /<hh:margin>[\s\S]*?<hc:left\b[^>]*\bvalue="(-?\d+)"/.exec(body)?.[1]
      ?? /<hh:intent\b[^>]*\bvalue="(-?\d+)"/.exec(body)?.[1];
    paraShapes.set(id, {
      id,
      alignment: align === 'CENTER' ? 'center'
        : align === 'RIGHT' ? 'right'
          : align === 'JUSTIFY' || align === 'BOTH' ? 'justify'
            : align === 'LEFT' ? 'left' : null,
      indentPx: left ? Math.max(0, Number(left) / HWPUNIT_PER_PX) : 0,
      numbered: /\bheadingType="NUMBER"/.test(attrs) || /<hh:heading\b[^>]*\btype="NUMBER"/.test(body),
    });
  }

  const styles = new Map<string, StyleInfo>();
  for (const match of headerXml.matchAll(/<hh:style\b([^>]*)\/?>/g)) {
    const attrs = match[1]!;
    const id = attr(attrs, 'id');
    const name = attr(attrs, 'name') ?? attr(attrs, 'engName') ?? '';
    if (!id) continue;
    styles.set(id, { id, name, headingLevel: headingLevelOf(name, attr(attrs, 'engName') ?? '') });
  }

  const fonts = new Map<string, string>();
  for (const match of headerXml.matchAll(/<hh:font\b([^>]*)\/?>/g)) {
    const attrs = match[1]!;
    const id = attr(attrs, 'id');
    const face = attr(attrs, 'face');
    if (id && face) fonts.set(id, face);
  }

  return { charShapes, paraShapes, styles, fonts };
}

/**
 * Hangul names its heading styles "개요 1"…"개요 7" and its title style "제목".
 * The English names a bilingual document carries are `Outline 1` and `Title`.
 */
function headingLevelOf(name: string, engName: string): number | null {
  const outline = /^(?:개요|Outline)\s*([1-7])$/i.exec(name) ?? /^Outline\s*([1-7])$/i.exec(engName);
  if (outline) return Math.min(6, Number(outline[1]));
  if (/^(제목|Title)$/i.test(name) || /^Title$/i.test(engName)) return 1;
  return null;
}

/* ------------------------------------------------------------------ *
 * section body
 * ------------------------------------------------------------------ */

export interface SectionParseOptions {
  readonly part: string;
  /** `binaryItemIDRef` → BinData part name, from the manifest. */
  readonly binaryItems: ReadonlyMap<string, string>;
  readonly maxBlocks: number;
  /** Paragraph index to start numbering from — sections continue each other. */
  readonly indexOffset: number;
}

function parseRuns(
  paragraphXml: string, options: SectionParseOptions,
): { runs: HwpxRun[]; editable: boolean } {
  const runs: HwpxRun[] = [];
  let editable = true;

  for (const runMatch of paragraphXml.matchAll(/<hp:run\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/hp:run>)/g)) {
    const charPrId = attr(runMatch[1]!, 'charPrIDRef');
    const body = runMatch[2] ?? '';

    // The attribute capture is lazy on purpose. Greedy, it consumes the `/` of
    // a self-closing tag, then matches the alternation's `>` branch and hunts
    // for a closing tag that belongs to a *later* element — swallowing
    // everything in between.
    const CHILD = /<hp:(t|tab|lineBreak|pic|equation|chart|ole|container|ctrl)\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/hp:\1>)/g;
    for (const child of body.matchAll(CHILD)) {
      const name = child[1]!;
      const attrs = child[2] ?? '';
      const inner = child[3] ?? '';
      switch (name) {
        case 't':
          runs.push({ kind: 'text', text: unescapeXml(inner), charPrId });
          break;
        case 'tab':
          runs.push({ kind: 'tab' });
          break;
        case 'lineBreak':
          runs.push({ kind: 'break' });
          break;
        case 'pic': {
          const itemId = /<hc:img\b[^>]*\bbinaryItemIDRef="([^"]+)"/.exec(inner)?.[1] ?? '';
          const size = /<hp:sz\b[^>]*\bwidth="(\d+)"[^>]*\bheight="(\d+)"/.exec(inner)
            ?? /<hc:sz\b[^>]*\bwidth="(\d+)"[^>]*\bheight="(\d+)"/.exec(inner);
          runs.push({
            kind: 'image',
            itemId,
            part: itemId ? options.binaryItems.get(itemId) ?? null : null,
            widthPx: size ? Math.round(Number(size[1]) / HWPUNIT_PER_PX) : 0,
            heightPx: size ? Math.round(Number(size[2]) / HWPUNIT_PER_PX) : 0,
            alt: attr(attrs, 'alt') ?? attr(attrs, 'desc') ?? '',
          });
          editable = false;
          break;
        }
        default:
          runs.push({ kind: 'object', what: name });
          editable = false;
          break;
      }
    }
  }

  return { runs, editable };
}

function parseParagraph(
  xml: string, start: number, end: number, index: number, options: SectionParseOptions,
): HwpxParagraph {
  const source = xml.slice(start, end);
  const openTag = source.slice(0, source.indexOf('>') + 1);
  const { runs, editable } = parseRuns(source, options);

  const text = runs.map((r) => (
    r.kind === 'text' ? r.text : r.kind === 'tab' ? '\t' : r.kind === 'break' ? '\n' : ''
  )).join('');

  return {
    kind: 'paragraph',
    index,
    part: options.part,
    charPrId: runs.find((r) => r.kind === 'text')?.charPrId ?? null,
    paraPrId: attr(openTag, 'paraPrIDRef'),
    styleId: attr(openTag, 'styleIDRef'),
    runs,
    text,
    originalText: text,
    editable,
    start,
    end,
  };
}

export function parseSection(xml: string, options: SectionParseOptions): ParsedSection {
  const blocks: HwpxBlock[] = [];
  const paragraphs: HwpxParagraph[] = [];
  let truncated = false;
  let cursor = 0;

  while (cursor < xml.length) {
    if (blocks.length >= options.maxBlocks) { truncated = true; break; }

    const nextP = indexOfElement(xml, '<hp:p', cursor, xml.length);
    if (nextP < 0) break;
    const end = endOfElement(xml, nextP, 'hp:p');
    const source = xml.slice(nextP, end);

    // A table lives inside a paragraph's run, and its cells hold paragraphs of
    // their own. Treating it as an ordinary paragraph would flatten the table
    // into a single line of concatenated cell text.
    const tableAt = source.indexOf('<hp:tbl');
    if (tableAt >= 0) {
      blocks.push(parseTable(xml, nextP + tableAt, end, paragraphs, options));
      cursor = end;
      continue;
    }

    const paragraph = parseParagraph(
      xml, nextP, end, options.indexOffset + paragraphs.length, options,
    );
    paragraphs.push(paragraph);
    blocks.push(paragraph);
    cursor = end;
  }

  return { blocks, paragraphs, truncated };
}

function parseTable(
  xml: string, start: number, limit: number,
  paragraphs: HwpxParagraph[], options: SectionParseOptions,
): HwpxTable {
  const end = endOfElement(xml, start, 'hp:tbl');
  const rows: HwpxTableCell[][] = [];
  let rowCursor = start;

  while (rowCursor < end) {
    const rowStart = indexOfElement(xml, '<hp:tr', rowCursor, end);
    if (rowStart < 0) break;
    const rowEnd = endOfElement(xml, rowStart, 'hp:tr');

    const cells: HwpxTableCell[] = [];
    let cellCursor = rowStart;
    while (cellCursor < rowEnd) {
      const cellStart = indexOfElement(xml, '<hp:tc', cellCursor, rowEnd);
      if (cellStart < 0) break;
      const cellEnd = endOfElement(xml, cellStart, 'hp:tc');
      const cellXml = xml.slice(cellStart, cellEnd);
      const span = /<hp:cellSpan\b([^>]*)/.exec(cellXml)?.[1] ?? '';

      const cellParagraphs: HwpxParagraph[] = [];
      let inner = cellStart;
      while (inner < cellEnd) {
        const pStart = indexOfElement(xml, '<hp:p', inner, cellEnd);
        if (pStart < 0) break;
        const pEnd = endOfElement(xml, pStart, 'hp:p');
        const paragraph = parseParagraph(
          xml, pStart, pEnd, options.indexOffset + paragraphs.length, options,
        );
        paragraphs.push(paragraph);
        cellParagraphs.push(paragraph);
        inner = pEnd;
      }

      cells.push({
        paragraphs: cellParagraphs,
        colSpan: Number(attr(span, 'colSpan') ?? 1),
        rowSpan: Number(attr(span, 'rowSpan') ?? 1),
      });
      cellCursor = cellEnd;
    }

    rows.push(cells);
    rowCursor = rowEnd;
  }

  return { kind: 'table', rows, start, end: Math.min(end, limit) };
}

/* ------------------------------------------------------------------ *
 * manifest
 * ------------------------------------------------------------------ */

/**
 * Section parts in reading order, from `Contents/content.hpf`.
 *
 * The spine decides the order, the same way it does in EPUB — file names are
 * not authoritative and `section10.xml` would otherwise sort before
 * `section2.xml`.
 */
export function parseContentHpf(hpfXml: string): {
  sections: string[];
  binaryItems: Map<string, string>;
} {
  const hrefById = new Map<string, string>();
  const mediaById = new Map<string, string>();
  for (const match of hpfXml.matchAll(/<opf:item\b([^>]*)\/?>/g)) {
    const attrs = match[0];
    const id = attr(attrs, 'id');
    const href = attr(attrs, 'href');
    if (!id || !href) continue;
    hrefById.set(id, href.startsWith('Contents/') || href.startsWith('BinData/')
      ? href
      : `Contents/${href.replace(/^\.\//, '')}`);
    mediaById.set(id, attr(attrs, 'media-type') ?? '');
  }

  const sections: string[] = [];
  for (const match of hpfXml.matchAll(/<opf:itemref\b([^>]*)\/?>/g)) {
    const idref = attr(match[0], 'idref');
    if (!idref) continue;
    sections.push(hrefById.get(idref) ?? `Contents/${idref}.xml`);
  }

  const binaryItems = new Map<string, string>();
  for (const [id, href] of hrefById) {
    if (href.startsWith('BinData/') || (mediaById.get(id) ?? '').startsWith('image/')) {
      binaryItems.set(id, href.startsWith('BinData/') ? href : `BinData/${href.split('/').pop()}`);
    }
  }

  return { sections, binaryItems };
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface HwpxEdit {
  readonly index: number;
  readonly text: string;
}

export interface HwpxEditResult {
  readonly xml: string;
  readonly dropped: readonly string[];
  readonly failed: readonly number[];
}

/**
 * Rewrite the text of specific paragraphs inside one section part.
 *
 * The paragraph's opening tag — with its `paraPrIDRef`, `styleIDRef` and id —
 * is kept verbatim, and the new text is placed in a single run carrying the
 * *same* `charPrIDRef` the first run had. Nothing in `header.xml` is touched,
 * so every other reference to that shape still means what it meant.
 */
export function applyHwpxParagraphEdits(
  xml: string, paragraphs: readonly HwpxParagraph[], edits: readonly HwpxEdit[],
): HwpxEditResult {
  const dropped: string[] = [];
  const failed: number[] = [];
  const byIndex = new Map(paragraphs.map((p) => [p.index, p]));

  const ordered = [...edits].sort((a, b) =>
    (byIndex.get(b.index)?.start ?? -1) - (byIndex.get(a.index)?.start ?? -1));

  let out = xml;
  for (const edit of ordered) {
    const paragraph = byIndex.get(edit.index);
    if (!paragraph || !paragraph.editable) { failed.push(edit.index); continue; }

    const source = out.slice(paragraph.start, paragraph.end);
    if (!source.startsWith('<hp:p')) { failed.push(edit.index); continue; }

    const charPrIds = new Set(paragraph.runs
      .filter((r) => r.kind === 'text')
      .map((r) => (r.kind === 'text' ? r.charPrId : null)));
    if (charPrIds.size > 1) {
      dropped.push(`문단 ${edit.index + 1}: 문단 안의 서로 다른 글자 모양이 첫 모양으로 통일됨`);
    }

    out = out.slice(0, paragraph.start)
      + rebuildParagraph(source, edit.text, paragraph.charPrId)
      + out.slice(paragraph.end);
  }

  return { xml: out, dropped, failed };
}

function rebuildParagraph(source: string, text: string, charPrId: string | null): string {
  const openTagEnd = source.indexOf('>');
  if (openTagEnd < 0) throw new HandlerError('error.unableToRead', 'malformed hp:p element');
  const openTag = source.slice(0, openTagEnd + 1).replace(/\/>$/, '>');
  const runAttr = charPrId === null ? '' : ` charPrIDRef="${escapeXml(charPrId)}"`;

  // `<hp:linesegarray>` holds cached line-break positions from the last time
  // Hangul laid the paragraph out. Dropping it is correct: keeping stale
  // geometry for text that changed would be worse than making the application
  // recompute it, which it does on open anyway.
  const body = text.split('\n').map((line, i) => {
    const segments = line.split('\t')
      .map((segment) => (segment === '' ? '' : `<hp:t>${escapeXml(segment)}</hp:t>`))
      .join('<hp:tab/>');
    return (i === 0 ? '' : '<hp:lineBreak/>') + segments;
  }).join('');

  return `${openTag}<hp:run${runAttr}>${body}</hp:run></hp:p>`;
}
